import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ViewerSearchParams, ViewerSearchResult } from '../models/viewer-search-control';
import { LocalizeService } from '../services/localize.service';
import { ViewerSearchService } from '../services/viewer-search.service';
import { UserInterface, Util } from '../utils/utils.module';

@Component({
  selector: 'edx-viewer-search',
  styleUrls: ['viewer-search.component.scss'],
  templateUrl: 'viewer-search.component.html',
  standalone: false
})

export class ViewerSearchComponent {
  @ViewChild('viewerSearchTextInput') viewerSearchTextInput: ElementRef;
  @Input('parent') parent: ViewerSearchOperations;
  public viewerSearchPlaceHolder: string;
  public viewerSearchEnabled: boolean = true;
  public currentIndexOfTotalResults: string;
  public viewerSearchParams: ViewerSearchParams;
  private viewerSearchResult: ViewerSearchResult;
  public matchCaseText: string = '';
  public matchWholeWordText: string = '';
  public previousMatchText: string = '';
  public nextMatchText: string = '';
  public searchResultText: string = '';
  public ui: UserInterface;
  public isAddin: boolean;
  public isMobile: boolean;

  constructor(private localizer: LocalizeService, private searchService: ViewerSearchService) {
    this.viewerSearchPlaceHolder = this.localizer.getTranslation('ALT_TEXT.SEARCH_TEXT');
    this.matchCaseText = this.localizer.getTranslation('ALT_TEXT.MATCH_CASE');
    this.matchWholeWordText = this.localizer.getTranslation('ALT_TEXT.MATCH_WHOLE_WORD');
    this.previousMatchText = this.localizer.getTranslation('ALT_TEXT.PREVIOUS_MATCH');
    this.nextMatchText = this.localizer.getTranslation('ALT_TEXT.NEXT_MATCH');
    this.resetViewerSearchParams();
    this.ui = Util.Device.ui;
    this.isAddin = Util.Device.bIsAddin;
    this.isMobile = Util.Device.isMobile();
  }

  private getViewerDocument(): Document {
    if (this.parent?.getViewerDocument) {
      return this.parent.getViewerDocument();
    }
    return null;
  }

  private resetToOriginal(): void {
    if (this.parent?.resetToOriginalContent) {
      this.parent.resetToOriginalContent();
    }
  }

  private getViewerSearchBar(): any {
    return this.viewerSearchTextInput?.nativeElement;
  }

  private setViewerSearchBar(searchText: string): void {
    const searchBar = this.getViewerSearchBar();
    if (searchBar) {
      searchBar.value = searchText;
    }
  }

  private resetViewerSearchBar(): void {
    const searchBar = this.getViewerSearchBar();
    if (searchBar) {
      searchBar.value = '';
    }
  }

  private resetViewerSearchParams(): void {
    this.viewerSearchParams = {
      searchText: '',
      matchWholeWord: true
    };
  }

  private resetViewerSearchInfo(): void {
    this.currentIndexOfTotalResults = '';
    this.viewerSearchResult = null;
    this.resetViewerSearchParams();
  }

  private setSearchResultContent(): void {
    this.currentIndexOfTotalResults = this.viewerSearchResult?.currentIndexOfTotal ?? '';
    if (this.viewerSearchResult?.totalOccurances > 0 && this.viewerSearchResult?.currentIndex > 0) {
      this.searchResultText = this.localizer.getTranslation('ALT_TEXT.SEARCH_RESULT_TEXT', [this.viewerSearchResult?.currentIndex.toString(), this.viewerSearchResult?.totalOccurances.toString()]);
    } else {
      this.searchResultText = this.localizer.getTranslation('ALT_TEXT.NO_RESULT_TEXT');
    }
  }

  public searchContentByGlobalText(iFrameDoc: any): void {
    //1. Reset search bar and results Info
    this.resetViewerSearchBar();
    this.resetViewerSearchInfo();
    //2. Get global search text
    let searchText: string = '';
    const currentSet: any = Util.RestAPI.getCurSet();
    if (currentSet && currentSet.search && currentSet.search.criteria) {
      const criteria = currentSet.search.criteria;
      searchText = criteria['FULLTEXT_CONTENT_PROFILE'] || criteria['FULLTEXT_CONTENT'] || criteria['FULLTEXT_PROFILE'];
      searchText = searchText || criteria['FULLTEXT_EDIT'];
    }

    if (!searchText || searchText?.length <= 0 || Util.Transforms.metadataInSearchText(searchText)) {
      return;
    }
    //3. Set search bar text
    this.setViewerSearchBar(searchText);
    this.viewerSearchParams = {
      searchText: searchText,
      matchWholeWord: true
    };
    //4. Perform search and update results
    this.performSearchOnContent(iFrameDoc);
  }

  private performSearchOnContent(iFrameDoc: any, restoreOriginal: boolean = false): any {
    if (!this.viewerSearchParams?.searchText || this.viewerSearchParams?.searchText?.length <= 0 || !iFrameDoc || !iFrameDoc?.body) {
      return;
    }
    //1. Restore original content
    if (restoreOriginal) {
      this.resetToOriginal();
    }
    //2. Perform search
    this.viewerSearchResult = this.searchService.highlightSearchText(iFrameDoc, this.viewerSearchParams);
    //3. Update search results
    this.setSearchResultContent();
  }

  private processSearchInput(canTraverse: boolean = false) {
    const viewerDocument = this.getViewerDocument();
    if (!viewerDocument || !viewerDocument?.body) {
      return;
    }
    const searchText = this.getViewerSearchBar()?.value;
    const isSearchTextChanged = this.viewerSearchParams.searchText !== searchText;
    if (!searchText && isSearchTextChanged) {
      this.resetViewerSearchInfo();
      this.resetToOriginal();
      return;
    }

    this.viewerSearchParams.searchText = searchText;
    if (canTraverse && !isSearchTextChanged) {
      this.handleTraversal();
    } else {
      this.performSearchOnContent(viewerDocument, true);
    }
  }

  protected viewerSearchTextKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Enter' && event.keyCode !== 13) {
      return;
    }
    this.processSearchInput(true);
  }
  protected toggleCaseMatch(): void {
    this.viewerSearchParams.matchCase = !(this.viewerSearchParams.matchCase ?? false);
    this.processSearchInput();
  }
  protected toggleWholeWordMatch(): void {
    this.viewerSearchParams.matchWholeWord = !(this.viewerSearchParams.matchWholeWord ?? false);
    this.processSearchInput();
  }
  protected handleTraversal(up: boolean = false): void {
    this.searchService.handleTraversal(this.viewerSearchResult, up, this.getViewerDocument());
    this.setSearchResultContent();
  }
}

export interface ViewerSearchOperations {
  resetToOriginalContent?(): void;
  getViewerDocument?(): Document;
}
